/**
 * New backlog item, two ways: write it yourself (kind, title, description, acceptance criteria,
 * defect details, estimate, labels, status, priority, owner, parent, attachments), or let AI
 * write it from one line and any screenshots or files (a screenshot of something broken becomes
 * a defect with repro steps). The AI's draft opens for review before it is created.
 *
 * Layout (missions vision): the title first, the description, then one row of value chips
 * (kind, status, priority, points, severity for a defect, parent); criteria, defect details,
 * owner, labels and files fold under More, whose closed line says what it holds.
 */
import { ChevronRight, PenLine, Sparkles } from '@orgus/ui/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import {
  ITEM_KINDS,
  ITEM_KIND_LABEL,
  ITEM_PRIORITIES,
  ITEM_PRIORITY_LABEL,
  ITEM_WORKFLOW,
  SEVERITIES,
  SEVERITY_LABEL,
  initialStatus,
  parseCriteria,
  statusLabel,
} from '@orgus/protocol';
import type { BacklogItem, Id, ItemKind, ItemPriority, ItemStatus, Project, Severity } from '@orgus/protocol';
import { AttachmentInput, AttachmentList, Button, Dialog, ErrorState, Input, MarkdownEditor, ProjectMark, Pressable, Segmented, Textarea, cn, hasMod, plural, toast } from '@orgus/ui';
import type { AttachmentView } from '@orgus/ui';
import { toUiError } from '../../lib/api.ts';
import type { UiError } from '../../lib/api.ts';
import { attachTo, removeAttachment, stripAttachmentRef, uploaderFor, useAttachmentsState, useResolveAttachment } from '../attachments/data.ts';
import { AiRunOptions, useAiRunOptions } from '../requirements/AiRunOptions.tsx';
import { startAiTask } from '../requirements/data.ts';
import { parentOptions } from '../requirements/model.ts';
import { SeverityMark } from '../requirements/parts.tsx';
import { createItem } from './data.ts';
import { ItemStatusIcon, KindIcon, PriorityMark } from './parts.tsx';
import { ValueChip } from './ValueChip.tsx';

/** The points a chip offers: the usual planning scale. */
const POINTS = [1, 2, 3, 5, 8, 13] as const;

export interface ItemDraft {
  projectId?: Id;
  status?: ItemStatus;
  parentId?: Id | null;
  kind?: ItemKind;
  /** Open on "Let AI write it". */
  ai?: boolean;
}

export function ItemDialog({
  open,
  onOpenChange,
  draft,
  projects,
  items,
  onCreated,
  onAiStarted,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  draft: ItemDraft;
  projects: Project[];
  items: readonly BacklogItem[];
  /** The item was created; `problem` says what did not work after that (its files were not attached). */
  onCreated: (item: BacklogItem, problem?: UiError) => void;
  onAiStarted?: (jobId: Id) => void;
}) {
  const [mode, setMode] = useState<'manual' | 'ai'>('manual');
  const [projectId, setProjectId] = useState('');
  const [kind, setKind] = useState<ItemKind>('story');
  const [aiKind, setAiKind] = useState<ItemKind | ''>('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [criteria, setCriteria] = useState('');
  const [request, setRequest] = useState('');
  const [status, setStatus] = useState<ItemStatus>('backlog');
  const [priority, setPriority] = useState<ItemPriority>(2);
  const [owner, setOwner] = useState('');
  const [parentId, setParentId] = useState('');
  const [estimate, setEstimate] = useState('');
  const [labels, setLabels] = useState('');
  const [severity, setSeverity] = useState<Severity>('major');
  const [steps, setSteps] = useState('');
  const [expected, setExpected] = useState('');
  const [actual, setActual] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const [more, setMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const titleRef = useRef<HTMLInputElement>(null);
  const requestRef = useRef<HTMLTextAreaElement>(null);
  const [files, setFiles] = useState<AttachmentView[]>([]);
  const filesOn = !!useAttachmentsState();
  const upload = useMemo(() => uploaderFor(null), []);
  const resolve = useResolveAttachment(files);
  const run = useAiRunOptions(open && mode === 'ai');

  useEffect(() => {
    if (!open) return;
    const k = draft.kind ?? 'story';
    setMode(draft.ai ? 'ai' : 'manual');
    setProjectId(draft.projectId ?? projects[0]?.id ?? '');
    setKind(k);
    setAiKind(draft.kind ?? '');
    setTitle('');
    setBody('');
    setCriteria('');
    setRequest('');
    setStatus(draft.status && ITEM_WORKFLOW[k].includes(draft.status) ? draft.status : initialStatus(k));
    setPriority(2);
    setOwner('');
    setParentId(draft.parentId ?? '');
    setEstimate('');
    setLabels('');
    setSeverity('major');
    setSteps('');
    setExpected('');
    setActual('');
    setShowErrors(false);
    setMore(false);
    setError(null);
    setBusy(false);
    setFiles([]);
    // Only on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const parents = useMemo(() => (projectId ? parentOptions(items, { kind: mode === 'ai' ? (aiKind || 'story') : kind, projectId }) : []), [items, projectId, kind, aiKind, mode]);
  const parent = parents.find((p) => p.id === parentId);
  const parsedCriteria = useMemo(() => parseCriteria(criteria), [criteria]);
  const errors = {
    project: projectId ? undefined : projects.length ? 'Choose a project.' : 'No projects are watched yet. Add one in Projects first.',
    title: mode === 'ai' ? undefined : !title.trim() ? 'Give the item a title.' : title.length > 200 ? 'Keep the title under 200 characters.' : undefined,
    request: mode === 'manual' ? undefined : !request.trim() && !files.length ? 'Say in a line what you need, or attach a screenshot.' : request.length > 4000 ? 'Keep it under 4,000 characters.' : undefined,
    estimate: estimate.trim() && !(Number(estimate) >= 0) ? 'Points are a number.' : undefined,
  };
  const valid = !errors.project && !errors.title && !errors.request && !errors.estimate;

  const changeKind = (k: ItemKind) => {
    setKind(k);
    setStatus((s) => (ITEM_WORKFLOW[k].includes(s) ? s : initialStatus(k)));
    if (parentId && !parentOptions(items, { kind: k, projectId }).some((p) => p.id === parentId)) setParentId('');
  };

  const submit = async () => {
    setShowErrors(true);
    if (!valid || busy) {
      if (errors.title) titleRef.current?.focus();
      if (errors.request) requestRef.current?.focus();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (mode === 'ai') {
        const r = await startAiTask({ task: 'draft', projectId, request: request.trim(), ...(aiKind ? { kind: aiKind } : {}), ...(parentId ? { parentId } : {}), ...(files.length ? { attachmentIds: files.map((f) => f.id) } : {}), ...run.value });
        toast.success('Writing it', 'The draft opens for review when it is ready.');
        onAiStarted?.(r.job.id);
        onOpenChange(false);
        return;
      }
      const it = await createItem({
        projectId,
        kind,
        title: title.trim(),
        body,
        status,
        priority,
        owner: owner.trim() || null,
        parentId: parentId && parents.some((p) => p.id === parentId) ? parentId : null,
        ...(parsedCriteria.length ? { acceptance: parsedCriteria } : {}),
        ...(estimate.trim() ? { estimate: Number(estimate) } : {}),
        ...(labels.trim() ? { labels: labels.split(',').map((l) => l.trim()).filter(Boolean) } : {}),
        ...(kind === 'defect' ? { defect: { severity, steps, expected, actual } } : {}),
      });
      let problem: UiError | undefined;
      if (files.length) {
        try {
          await attachTo(files.map((f) => f.id), { kind: 'item', id: it.id });
        } catch (e) {
          const why = toUiError(e);
          problem = { what: `${it.key} was created without its files.`, why: why.why, next: 'Attach them again from the item.' };
        }
      }
      toast.success(`${ITEM_KIND_LABEL[it.kind]} ${it.key} added`, files.length && !problem ? `${it.title}, with ${files.length} ${files.length === 1 ? 'attachment' : 'attachments'}` : it.title);
      onCreated(it, problem);
      onOpenChange(false);
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && hasMod(e)) {
      e.preventDefault();
      void submit();
    }
  };

  const fileList = filesOn && (
    <div className="flex flex-col gap-2">
      <span className="text-caption text-fg-subtle">{mode === 'ai' ? 'Screenshots and files for the AI' : 'Attachments'}</span>
      <AttachmentList
        items={files}
        canRemove={() => true}
        onRemove={(a) => {
          setFiles((x) => x.filter((f) => f.id !== a.id));
          setBody((b) => stripAttachmentRef(b, a.id));
          void removeAttachment(a.id).catch(() => undefined);
        }}
      />
      <AttachmentInput upload={upload} onUploaded={(a) => setFiles((x) => [...x, a])} paste={open ? 'document' : 'none'} disabled={busy} hint="Paste a screenshot anywhere in this dialog." />
    </div>
  );

  // The project is the first value chip, its mark saying what it is; a missing one says so under the chips.
  const project = projects.find((p) => p.id === projectId);
  const projectChip = (
    <ValueChip
      label="Project"
      value={projectId}
      onChange={(v) => {
        setProjectId(v);
        setParentId('');
      }}
      disabled={!projects.length}
      icon={project ? <ProjectMark projectId={project.id} name={project.name} /> : undefined}
      display={project ? project.name : 'Choose a project'}
      options={projects.map((p) => ({ value: p.id, label: p.name, icon: <ProjectMark projectId={p.id} name={p.name} /> }))}
    />
  );
  const projectError = showErrors && errors.project ? <p className="text-caption text-status-danger">{errors.project}</p> : null;

  // QA-22: with nothing to nest under there is no choice to show, so no chip (it wrapped alone onto a
  // second row); a chosen parent's title is capped so the chip stays on the row with its siblings.
  const parentChip = parents.length > 0 && (
    <ValueChip
      label="Parent"
      value={parentId}
      onChange={setParentId}
      className="max-w-40"
      icon={parent ? <KindIcon kind={parent.kind} tooltip={false} /> : undefined}
      display={parent ? parent.title : 'No parent'}
      options={[{ value: '', label: 'No parent' }, ...parents.map((p) => ({ value: p.id, label: `${p.key} ${p.title}`, icon: <KindIcon kind={p.kind} tooltip={false} /> }))]}
    />
  );

  // What the folded part holds, in one quiet line (the New session dialog's Options pattern).
  const moreSummary =
    mode === 'ai'
      ? `${files.length ? plural(files.length, 'file') : 'Files'}, where it runs`
      : [
          parsedCriteria.length ? plural(parsedCriteria.length, 'criterion', 'criteria') : 'Criteria',
          owner.trim() ? owner.trim() : 'owner',
          labels.trim() ? plural(labels.split(',').filter((l) => l.trim()).length, 'label') : 'labels',
          kind === 'defect' ? 'repro steps' : null,
          files.length ? plural(files.length, 'file') : 'files',
        ]
          .filter(Boolean)
          .join(', ');

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="New item"
      size="lg"
      dismissible={!busy}
      initialFocus={() => (mode === 'ai' ? requestRef.current : titleRef.current)}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} onClick={submit} shortcut={['mod', 'enter']} icon={mode === 'ai' ? <Sparkles /> : undefined}>
            {mode === 'ai' ? 'Draft item' : 'Add item'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4" onKeyDown={onKeyDown}>
        <div className="flex flex-wrap items-center gap-3">
          <Segmented
            label="How"
            value={mode}
            onChange={(m) => {
              setMode(m);
              setShowErrors(false);
            }}
            options={[
              { value: 'manual', label: 'Write it', icon: <PenLine /> },
              { value: 'ai', label: 'With AI', icon: <Sparkles /> },
            ]}
          />
        </div>

        {mode === 'ai' ? (
          <>
            <Textarea
              ref={requestRef}
              label="What do you need?"
              rows={3}
              value={request}
              onChange={(e) => setRequest(e.target.value)}
              error={showErrors ? errors.request : undefined}
              hint="A screenshot of something broken becomes a defect."
            />
            <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Values">
              {projectChip}
              <ValueChip
                label="Kind"
                value={aiKind}
                onChange={(v) => setAiKind(v as ItemKind | '')}
                icon={aiKind ? <KindIcon kind={aiKind} tooltip={false} /> : <Sparkles className="size-4 text-fg-subtle" aria-hidden />}
                options={[{ value: '', label: 'AI decides the kind' }, ...ITEM_KINDS.map((k) => ({ value: k, label: ITEM_KIND_LABEL[k], icon: <KindIcon kind={k} tooltip={false} /> }))]}
              />
              {parentChip}
            </div>
            {projectError}
          </>
        ) : (
          <>
            <Input ref={titleRef} label="Title" value={title} onChange={(e) => setTitle(e.target.value)} error={showErrors ? errors.title : undefined} maxLength={200} />
            <MarkdownEditor
              label="Description"
              rows={4}
              value={body}
              onChange={setBody}
              maxLength={20_000}
              upload={filesOn ? upload : undefined}
              onUploaded={(a) => setFiles((x) => [...x, a])}
              resolveAttachment={resolve}
            />
            {/* The values that matter, as one row of chips; each opens its choices. */}
            <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Values">
              {projectChip}
              <ValueChip label="Kind" value={kind} onChange={(v) => changeKind(v as ItemKind)} options={ITEM_KINDS.map((k) => ({ value: k, label: ITEM_KIND_LABEL[k], icon: <KindIcon kind={k} tooltip={false} /> }))} />
              <ValueChip label="Status" value={status} onChange={(v) => setStatus(v as ItemStatus)} options={ITEM_WORKFLOW[kind].filter((s) => s !== 'dropped').map((s) => ({ value: s, label: statusLabel(kind, s), icon: <ItemStatusIcon status={s} /> }))} />
              <ValueChip label="Priority" value={String(priority)} onChange={(v) => setPriority(Number(v) as ItemPriority)} options={ITEM_PRIORITIES.map((p) => ({ value: String(p), label: ITEM_PRIORITY_LABEL[p], icon: <PriorityMark priority={p} /> }))} />
              <ValueChip
                label="Points"
                value={estimate}
                onChange={setEstimate}
                display={estimate ? plural(Number(estimate), 'point') : 'No points'}
                options={[{ value: '', label: 'No points' }, ...POINTS.map((n) => ({ value: String(n), label: plural(n, 'point') }))]}
              />
              {kind === 'defect' && (
                <ValueChip label="Severity" value={severity} onChange={(v) => setSeverity(v as Severity)} options={SEVERITIES.map((s) => ({ value: s, label: SEVERITY_LABEL[s], icon: <SeverityMark severity={s} label={false} /> }))} />
              )}
              {parentChip}
            </div>
            {projectError}
          </>
        )}

        {/* Everything else, folded behind one quiet line that says what it holds. */}
        <div className="flex flex-col gap-4">
          <Pressable
            onClick={() => setMore(!more)}
            aria-expanded={more}
            className="flex min-w-0 items-center gap-2 self-stretch rounded-control text-left text-caption text-fg-subtle transition-colors duration-(--duration-fast) hover:text-fg-muted"
          >
            <ChevronRight className={cn('size-3 shrink-0 transition-transform duration-(--duration-fast)', more && 'rotate-90')} aria-hidden />
            <span className="shrink-0 font-medium text-fg-muted">More</span>
            {!more && <span className="min-w-0 truncate">{moreSummary}</span>}
          </Pressable>
          {more &&
            (mode === 'ai' ? (
              <div className="flex flex-col gap-4">
                {fileList}
                <AiRunOptions {...run} />
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {kind !== 'epic' && kind !== 'feature' && (
                  <Textarea
                    label="Acceptance criteria"
                    rows={3}
                    value={criteria}
                    onChange={(e) => setCriteria(e.target.value)}
                    hint={parsedCriteria.length ? `${plural(parsedCriteria.length, 'criterion', 'criteria')}` : 'One per line: Given, when, then, or a checklist line.'}
                  />
                )}
                {kind === 'defect' && (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Textarea label="Steps to reproduce" rows={3} value={steps} onChange={(e) => setSteps(e.target.value)} wrapperClassName="sm:col-span-2" />
                    <Textarea label="Expected" rows={2} value={expected} onChange={(e) => setExpected(e.target.value)} />
                    <Textarea label="Actual" rows={2} value={actual} onChange={(e) => setActual(e.target.value)} />
                  </div>
                )}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Input label="Owner" value={owner} onChange={(e) => setOwner(e.target.value)} maxLength={100} />
                  <Input label="Labels" hint="Comma separated" value={labels} onChange={(e) => setLabels(e.target.value)} />
                </div>
                {fileList}
              </div>
            ))}
        </div>
        {error && <ErrorState variant="inline" error={error} />}
      </div>
    </Dialog>
  );
}
